import { REP_SITE, STATE_GROUP_ID } from '@constants/index';
import type { PalmettoAccount, RepAccess, RepPermission } from '@/types';

/**
 * The portal packs site grants into the account's `attributes`:
 *
 *   [ { "REP": [ { "S": "ACT" }, { "<groupId>": [ { "S": "ACT" }, { "P": "F" } ] } ] }, ... ]
 *
 * The site-level `S` is the overall status; the per-group entry carries the
 * status (`S`) and the permission letter (`P`): F Full, L Limited, C CERT,
 * R REM, G Groups. Mirrors palmetto-typescript-rep-api libs/permissions.ts so
 * the UI hides exactly what the API refuses.
 */
type GrantValue = { S?: string; P?: string } | Record<string, unknown>;
type SiteGrant = Record<string, GrantValue[] | undefined>;

const LETTERS: RepPermission[] = ['F', 'L', 'C', 'R', 'G'];

export const resolveRepAccess = (account: PalmettoAccount | null | undefined): RepAccess => {
  const groupId = Number(account?.pvActiveGroupID ?? 0) || 0;
  const groupName =
    account?.currentGroups?.find((g) => Number(g.i) === groupId)?.n ??
    account?.account2groups?.find((g) => Number(g.pvGroupID) === groupId)?.pvGroupName ??
    null;
  const access: RepAccess = {
    status: 'DNY',
    type: undefined,
    groupId,
    groupName,
    isStateUser: groupId === STATE_GROUP_ID,
  };

  const attributes = unwrapAttributes(account?.attributes);
  const site = attributes.find((entry) => entry && Object.keys(entry)[0] === REP_SITE) as
    SiteGrant | undefined;
  if (!site) return access;
  const grants = site[REP_SITE] ?? [];

  for (const grant of grants) {
    const key = Object.keys(grant)[0];
    if (key === 'S') {
      access.status = String((grant as { S?: string }).S ?? 'DNY');
    } else if (!Number.isNaN(Number(key)) && Number(key) === groupId) {
      const values = ((grant as Record<string, unknown>)[key] ?? []) as GrantValue[];
      for (const value of Array.isArray(values) ? values : []) {
        const k = Object.keys(value)[0];
        if (k === 'S') access.status = String((value as { S?: string }).S ?? access.status);
        if (k === 'P') {
          const letter = String((value as { P?: string }).P ?? '').toUpperCase();
          if ((LETTERS as string[]).includes(letter)) access.type = letter as RepPermission;
        }
      }
    }
  }
  // A group grant without an explicit status inherits the site status; a
  // group entry with a permission letter is active by definition.
  if (access.type && access.status !== 'DNY') access.status = 'ACT';
  if (access.type && access.status === 'DNY') access.status = 'ACT';
  return access;
};

/** The portal sometimes returns `attributes` wrapped as `{ attributes: [...] }`. */
const unwrapAttributes = (raw: unknown): SiteGrant[] => {
  if (Array.isArray(raw)) return raw as SiteGrant[];
  if (
    raw &&
    typeof raw === 'object' &&
    Array.isArray((raw as { attributes?: unknown }).attributes)
  ) {
    return (raw as { attributes: SiteGrant[] }).attributes;
  }
  return [];
};

export const hasRepAccess = (access: RepAccess | null | undefined): boolean =>
  !!access && access.status === 'ACT' && !!access.type;

/** F or L: may create/update/delete program records (API `requireEditor`). */
export const isEditor = (access: RepAccess | null | undefined): boolean =>
  hasRepAccess(access) && (access!.type === 'F' || access!.type === 'L');

/** F only (API `requireFull`): catalogs, archive, agreement resets, deletes of state rows. */
export const isFull = (access: RepAccess | null | undefined): boolean =>
  hasRepAccess(access) && access!.type === 'F';

/** F, L or C: may edit EOPA documents. */
export const canEditEopa = (access: RepAccess | null | undefined): boolean =>
  hasRepAccess(access) && ['F', 'L', 'C'].includes(access!.type as string);

export const PERMISSION_LABELS: Record<RepPermission, string> = {
  F: 'Full access',
  L: 'Limited access',
  C: 'CERT',
  R: 'REM',
  G: 'Groups',
};
